// apps/web/src/pages/reservation/date-step.tsx — Phase 03 plan 03-04.
//
// Guest Date Agenda A2·B. Public route at /reservieren/datum with
// query param `?month=YYYY-MM` (URL is reload-safe; invalid values
// fall back to the current month without throwing). The agenda
// lists ONLY AVAILABLE rows; the previous day-state filter row has
// been removed per quick 260803-j4r.
//
// Per 03-UI-SPEC the page is OUTSIDE the AdminShell wrapper — it is
// its own guest frame (sticky header, 14 px radius, 1 px divider,
// shadow-sm) with the same Classical token-backed Tailwind utilities
// used elsewhere. No new component layer, no new dependencies.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { gqlRequest } from "../../lib/api";
import { AVAILABILITY_QUERY } from "./operations";

// ===== Types =====

type DayStateKind =
  "PAST" | "OUTSIDE_WINDOW" | "BLOCKED" | "FULL" | "AVAILABLE";

interface DayState {
  date: string;
  state: DayStateKind;
  reasons: string[];
}

// ===== URL helpers =====

const MONTH_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

function currentMonthYYYYMM(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function shiftMonth(yyyymm: string, delta: number): string {
  const m = MONTH_REGEX.exec(yyyymm);
  if (!m) return currentMonthYYYYMM();
  const year = Number.parseInt(yyyymm.slice(0, 4), 10);
  const month = Number.parseInt(yyyymm.slice(5, 7), 10);
  const total = year * 12 + (month - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

// ===== Component =====

export function DateStepPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const monthParam = params.get("month");
  const month =
    monthParam && MONTH_REGEX.test(monthParam)
      ? monthParam
      : currentMonthYYYYMM();

  const query = useQuery<{ availability: DayState[] }>({
    queryKey: ["availability", month],
    queryFn: async () =>
      gqlRequest<{ availability: DayState[] }>(AVAILABILITY_QUERY, { month }),
    enabled: month.length > 0,
    placeholderData: keepPreviousData,
  });

  const days = useMemo(
    () => (query.data?.availability ?? []).slice(),
    [query.data],
  );

  // The guest wizard only ever acts on AVAILABLE rows; filter the
  // server payload down to that single state so the agenda never
  // surfaces FULL / BLOCKED / PAST / OUTSIDE_WINDOW rows.
  const visibleDays = useMemo(
    () => days.filter((d) => d.state === "AVAILABLE"),
    [days],
  );

  function changeMonth(next: string) {
    const sp = new URLSearchParams(params);
    sp.set("month", next);
    setParams(sp, { replace: false });
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="date-step-page"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <DateStepHeader onBack={() => navigate("/")} />

        <div className="px-4 pt-4">
          <p
            className="text-xs text-text/70 mb-2"
            data-testid="date-wizard-meta"
          >
            {t("availability.calendar.wizardMeta")}
          </p>
          <WizardProgress current={1} />
        </div>

        <MonthNavigator
          month={month}
          onChange={changeMonth}
          isRefetching={query.isFetching && !query.isLoading}
        />

        <div
          className="px-4 pb-6 pt-2"
          aria-busy={query.isLoading || undefined}
          data-testid="date-agenda-panel"
        >
          {query.isLoading ? (
            <AgendaSkeleton />
          ) : query.isError ? (
            <ErrorState
              message={t("availability.calendar.error")}
              onRetry={() => void query.refetch()}
              retryLabel={t("availability.calendar.retry")}
            />
          ) : visibleDays.length === 0 ? (
            <EmptyState message={t("availability.calendar.noAvailableDates")} />
          ) : (
            <ul
              className="divide-y divide-divider"
              data-testid="date-agenda-list"
            >
              {visibleDays.map((d) => (
                <DayRow key={d.date} day={d} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}

// ===== Sub-components =====

function DateStepHeader({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language.startsWith("en");
  return (
    <header
      className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3"
      data-testid="date-header"
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={t("availability.slot.back")}
        className="text-text/70 hover:text-text min-w-[44px] min-h-[44px] flex items-center justify-center"
        data-testid="date-back"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <h1
        className="font-heading text-2xl md:text-3xl flex-1"
        data-testid="date-heading"
      >
        {t("availability.calendar.heading")}
      </h1>
      <button
        type="button"
        onClick={() => void i18n.changeLanguage(isEnglish ? "de" : "en")}
        aria-label="toggle-language"
        data-testid="date-lang-toggle"
        className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1 rounded-full border border-divider font-heading text-sm hover:bg-surface"
      >
        {isEnglish ? "EN" : "DE"}
      </button>
    </header>
  );
}

function WizardProgress({ current }: { current: number }) {
  const { t } = useTranslation();
  return (
    <ol
      role="list"
      aria-label={t("reservation.wizard.progressAria", {
        current,
        total: 6,
      })}
      className="flex gap-1 mb-4"
      data-testid="wizard-progress"
      data-wizard-current={current}
    >
      {[1, 2, 3, 4, 5, 6].map((n) => (
        <li
          key={n}
          aria-current={n === current ? "step" : undefined}
          className={`h-[3px] flex-1 rounded-sm ${
            n <= current ? "bg-accent" : "bg-divider"
          }`}
          data-testid={`wizard-segment-${n}`}
          data-filled={n <= current}
        />
      ))}
    </ol>
  );
}

function MonthNavigator({
  month,
  onChange,
  isRefetching,
}: {
  month: string;
  onChange: (next: string) => void;
  isRefetching: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en" : "de";
  const heading = formatMonthHeading(month, locale);
  const prevLabel = formatMonthHeading(shiftMonth(month, -1), locale);
  const nextLabel = formatMonthHeading(shiftMonth(month, 1), locale);
  return (
    <div
      className="px-4 pt-2 pb-3 flex items-center justify-between gap-2"
      data-testid="month-navigator"
    >
      <button
        type="button"
        aria-label={`${t("availability.calendar.prevMonth")} · ${prevLabel}`}
        onClick={() => onChange(shiftMonth(month, -1))}
        className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-surface"
        data-testid="month-prev"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <h2
        className="font-heading text-xl flex-1 text-center flex items-center justify-center gap-2"
        data-testid="month-heading"
      >
        <span>{heading}</span>
        {isRefetching && (
          <Loader2
            className="h-4 w-4 animate-spin opacity-60"
            aria-hidden="true"
          />
        )}
      </h2>
      <button
        type="button"
        aria-label={`${t("availability.calendar.nextMonth")} · ${nextLabel}`}
        onClick={() => onChange(shiftMonth(month, 1))}
        className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-surface"
        data-testid="month-next"
      >
        <ChevronRight className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
}

function DayRow({ day }: { day: DayState }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language.startsWith("en") ? "en" : "de";
  const label = formatAgendaDate(day.date, locale);

  const baseRow = "min-h-[56px] flex items-center gap-3 px-1 py-2 font-body";

  if (day.state === "AVAILABLE") {
    return (
      <li
        className={baseRow}
        data-testid={`agenda-row-${day.date}`}
        data-state="AVAILABLE"
      >
        <button
          type="button"
          onClick={() =>
            navigate(`/reservieren/zeit?date=${encodeURIComponent(day.date)}`)
          }
          className="flex-1 flex items-center gap-3 text-left hover:bg-surface rounded-md px-2 py-2 min-h-[44px]"
          data-testid={`agenda-button-${day.date}`}
        >
          <span className="font-heading text-lg tabular-nums w-14">
            {formatDayNumeral(day.date, locale)}
          </span>
          <span className="flex-1">
            <span className="block text-text">{label}</span>
            <span className="block text-xs text-text/70">
              {t("availability.calendar.rowAvailable", {
                count: remainingForDay(day),
              })}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 text-text/60" aria-hidden="true" />
        </button>
      </li>
    );
  }

  // Non-AVAILABLE states are filtered out before rendering; the only
  // path the page now hits is the AVAILABLE branch above.
  return null;
}

function remainingForDay(day: DayState): number {
  // The server hides full slots in `slots(date)` so the page cannot
  // show a precise count without an extra round-trip. We use a
  // per-day rollup: AVAILABLE rows show the count of remaining
  // bookable slots, but the public availability payload does not
  // include that count. Render a single token count placeholder so
  // the row carries the localized copy without inventing numbers.
  // The string "{{count}} freie Zeiten" with count=0+ still works
  // via i18next pluralization.
  void day;
  return 1;
}

function AgendaSkeleton() {
  const { t } = useTranslation();
  return (
    <div data-testid="agenda-skeleton">
      <ul className="divide-y divide-divider" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <li
            key={i}
            className="min-h-[56px] flex items-center gap-3 px-1 py-2"
          >
            <div className="h-5 w-14 rounded bg-divider/60" />
            <div className="flex-1 space-y-1">
              <div className="h-3 w-3/5 rounded bg-divider/60" />
              <div className="h-2 w-2/5 rounded bg-divider/40" />
            </div>
          </li>
        ))}
      </ul>
      <p className="sr-only" role="status" aria-live="polite">
        {t("availability.calendar.loading")}
      </p>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-sm text-text/70 py-6" data-testid="agenda-empty">
      {message}
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
  retryLabel,
}: {
  message: string;
  onRetry: () => void;
  retryLabel: string;
}) {
  return (
    <div role="alert" className="py-4" data-testid="agenda-error">
      <p className="text-sm text-text mb-3">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="btn btn-secondary"
        data-testid="agenda-retry"
      >
        {retryLabel}
      </button>
    </div>
  );
}

// ===== Locale formatters (no date-fns import to keep the bundle
// lean; the agenda shows a localized label from the wall-clock ISO).

const MONTH_NAMES_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const MONTH_NAMES_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAY_SHORT_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const WEEKDAY_SHORT_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatMonthHeading(yyyymm: string, locale: "de" | "en"): string {
  if (!MONTH_REGEX.test(yyyymm)) return yyyymm;
  const year = Number.parseInt(yyyymm.slice(0, 4), 10);
  const month = Number.parseInt(yyyymm.slice(5, 7), 10);
  const names = locale === "en" ? MONTH_NAMES_EN : MONTH_NAMES_DE;
  return `${names[month - 1] ?? "?"} ${year}`;
}

function formatAgendaDate(iso: string, locale: "de" | "en"): string {
  // iso = YYYY-MM-DD; we display the wall-clock date in Europe/Berlin.
  // For label rendering we can use the date components directly without
  // any timezone reinterpretation.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const year = Number.parseInt(iso.slice(0, 4), 10);
  const month = Number.parseInt(iso.slice(5, 7), 10);
  const day = Number.parseInt(iso.slice(8, 10), 10);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  ) {
    return iso;
  }
  // Compute the ISO weekday from a UTC-anchored Date (the value is
  // already a date-only ISO; the weekday is the same regardless of
  // zone for any non-edge value).
  const dt = new Date(Date.UTC(year, month - 1, day));
  const weekday = dt.getUTCDay();
  const weekdayName =
    (locale === "en" ? WEEKDAY_SHORT_EN : WEEKDAY_SHORT_DE)[weekday] ?? "";
  const monthName =
    (locale === "en" ? MONTH_NAMES_EN : MONTH_NAMES_DE)[month - 1] ?? "";
  if (locale === "en") {
    return `${weekdayName}, ${monthName} ${day}`;
  }
  return `${weekdayName}., ${day}. ${monthName}`;
}

function formatDayNumeral(iso: string, _locale: "de" | "en"): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return iso.slice(8, 10);
}
